import { ATHENA_REFERRAL_DOCUMENT_TYPE_IDS } from "@/configuration/ehr/athena/defaults";
import type { AthenaDepartmentConfig } from "@/configuration/ehr/athena/types";
import type { AthenaDocument } from "@/src/lib/ehr/athena/athena-types";
import { isStatOrUrgentPriority, type Diagnosis, type Referral } from "@/src/models/documents/referral";
import { formatICD10CodeForDisplay } from "@/src/models/icd10";

export function hasUrgentSchedulingPriority(referral: Referral): boolean {
  return isStatOrUrgentPriority(referral.schedulingPriority);
}

export const ATHENA_REFERRAL_DOCUMENT_LABELS = {
  ESTABLISHED_PATIENT: "Established Patient Referral",
  NEW_PATIENT: "New Patient Referral",
} as const;

export function getAthenaReferralDocumentLabel(isNewPatient: boolean): string {
  return isNewPatient
    ? ATHENA_REFERRAL_DOCUMENT_LABELS.NEW_PATIENT
    : ATHENA_REFERRAL_DOCUMENT_LABELS.ESTABLISHED_PATIENT;
}

export function referralDocumentTypeId(isNewPatient: boolean): number {
  return isNewPatient
    ? ATHENA_REFERRAL_DOCUMENT_TYPE_IDS.NEW_PATIENT
    : ATHENA_REFERRAL_DOCUMENT_TYPE_IDS.ESTABLISHED_PATIENT;
}

export function formatDiagnosisField(
  diagnosisList: Diagnosis[] | null | undefined,
  field: "diagnosisText" | "diagnosisCode"
): string {
  if (!diagnosisList) return "";

  if (field === "diagnosisCode") {
    return diagnosisList
      .map((diagnosis) => formatICD10CodeForDisplay(diagnosis[field]))
      .filter((value): value is string => Boolean(value?.trim()))
      .join(", ");
  }

  return diagnosisList
    .map((diagnosis) => diagnosis[field])
    .filter((value): value is string => Boolean(value?.trim()))
    .join(", ");
}

export function getDepartmentAssignee(
  departments: Record<string, AthenaDepartmentConfig>,
  redirects: Record<string, string>,
  document: AthenaDocument | null
): string | null {
  if (!document) return null;

  const departmentId = redirects[document.departmentid] ?? document.departmentid;

  return departments[departmentId]?.assignee ?? null;
}
